import cn from 'classnames';
import gtag from '../analytics/gtag';
import { getUserProductAvailability } from '../api/product';
import Icon from '../generic/Icon';
import { StoreStockLocatorModal } from '../generic/Modals';
import * as Product from '../model/Product';
import useAsyncMemo from '../utils/useAsyncMemo';
import * as style from './ProductAvailability.module.css';
import type { ProductCardDetails } from './ProductCard';

export interface Props {
    product: ProductCardDetails;
    forceRefresh?: boolean;
    large?: boolean;
}

export interface Availability {
    deliveryAvailability: ProductAvailabilityInfo;
    storesAvailability: ProductAvailabilityInfo;
}

export interface ProductAvailabilityInfo {
    availableForPurchase: boolean;
    infos: AvailabilityInfo[];
    openStockLocator: boolean;
    infoAvailabilityAllStores?: string;
}

export interface AvailabilityInfo {
    availability: string;
    location?: string;
    additionalInfo?: string;
    readableValue: string;
}

export default function ProductAvailability({ product, forceRefresh, large }: Props) {
    const [availability] = useAsyncMemo(
        async (availability, id, forceRefresh) => (!forceRefresh && availability) || (await getUserProductAvailability(id)),
        [product.productAvailability, product.code, forceRefresh],
        product.productAvailability,
    );

    if (!availability || Product.isGiftCard(product)) {
        return null;
    }

    const { deliveryAvailability, storesAvailability } = availability;

    const openModal = () => {
        StoreStockLocatorModal.call({ product });
        gtag('event', 'modal_view', {
            modal_name: 'StoreStockLocator',
            item_id: product.code,
        });
    };

    return (
        <>
            <div className={cn(style.productAvailability, { [style.large]: large })}>
                <Icon className={cn(style.iconStock, style.iconStore)} />
                <div>
                    {storesAvailability.infos.map((info) => (
                        <div key={info.location ?? '-'} aria-label={info.readableValue}>
                            {info.location && <strong>{info.location}: </strong>}
                            {info.availability}
                            {info.additionalInfo}
                        </div>
                    ))}
                    {storesAvailability.openStockLocator ? (
                        <button className="link" onClick={openModal}>
                            {storesAvailability.infoAvailabilityAllStores}
                        </button>
                    ) : (
                        <em>{storesAvailability.infoAvailabilityAllStores}</em>
                    )}
                </div>
                <Icon className={cn(style.iconStock, style.iconDelivery)} />
                <div>
                    {deliveryAvailability.infos.map((info) => (
                        <div key={info.location ?? '-'} aria-label={info.readableValue}>
                            {info.location && <strong>{info.location}: </strong>}
                            {info.availability}
                            {info.additionalInfo}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}
